import { useEffect, useRef, useState } from "react";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { useAppSelector } from "~/app/store";
import { ClientConfig, defaultClientConfig } from "~/config/clientConfig";
import { selectAuthHeaderReady } from "~/redux/selectors/appSelectors";
import {
  selectIsUserLoggedIn,
  selectAuth0Profile
} from "~/redux/selectors/authSelectors";
import {
  useGetClientConfigQuery,
  useCreateClientConfigMutation,
  useUpdateClientConfigMutation
} from "~/redux/warehouse/clientConfig.hooks";

export interface ClientConfigSyncResult {
  parseError: string | null;
}

/**
 * Syncs client configuration on user login - creates if missing, updates if outdated.
 *
 * Flow:
 * 1. Waits for user auth (isUserLoggedIn, auth0Profile, authHeaderReady)
 * 2. Performs GET to check if config exists
 * 3. If 404: POST to create config with defaults
 * 4. If 200: Check configVersion - if local version > server version, merge and update
 * 5. If 409: Concurrent creation/update occurred - silently ignored (another client handled it)
 *
 * Version-based updates:
 * - When configVersion in local config > server config, merge and update server
 * - Existing user settings are PRESERVED - only new fields get default values
 * - Increment configVersion in defaultClientConfig when adding new fields
 *
 * Timing: Executes during AuthCallback, BEFORE clientConfigReady is set to true.
 * This ensures configurations are populated before the app tries to use them.
 */
export function useClientConfigSync(): ClientConfigSyncResult {
  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const auth0Profile = useAppSelector(selectAuth0Profile);
  const authHeaderReady = useAppSelector(selectAuthHeaderReady);
  const hasProcessedRef = useRef(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const shouldQuery = isUserLoggedIn && !!auth0Profile && authHeaderReady;
  const clientId = auth0Profile ? getUserClientId(auth0Profile) : null;

  const {
    data: existingConfig,
    error: getError,
    isLoading
  } = useGetClientConfigQuery(undefined, {
    skip: !shouldQuery
  });

  const [createClientConfig] = useCreateClientConfigMutation();
  const [updateClientConfig] = useUpdateClientConfigMutation();

  useEffect(() => {
    if (!shouldQuery || isLoading || !clientId) {
      return;
    }

    if (hasProcessedRef.current) {
      return;
    }

    // 404 - config doesn't exist, create it
    if (
      getError &&
      typeof getError === "object" &&
      "status" in getError &&
      (getError as { status: number }).status === 404
    ) {
      hasProcessedRef.current = true;

      const requestPayload = {
        ClientConfiguration: JSON.stringify(defaultClientConfig)
      };

      createClientConfig(requestPayload)
        .unwrap()
        .catch((createError: unknown) => {
          // 409 Conflict - another process created it first (expected race condition)
          if (
            createError &&
            typeof createError === "object" &&
            "status" in createError &&
            (createError as { status: number }).status === 409
          ) {
            return;
          }
        });
      return;
    }

    if (existingConfig?.clientConfiguration && existingConfig.etag) {
      hasProcessedRef.current = true;

      try {
        const serverConfig = JSON.parse(
          existingConfig.clientConfiguration
        ) as Partial<ClientConfig>;

        const serverVersion = serverConfig.configVersion ?? 0;
        const localVersion = defaultClientConfig.configVersion ?? 1;

        if (localVersion > serverVersion) {
          // Merge: start with local defaults, overlay with server values (preserves user settings)
          // Then update configVersion to new version
          const mergedConfig: ClientConfig = {
            ...defaultClientConfig, // New defaults for any missing fields
            ...serverConfig, // User's existing settings take precedence
            configVersion: localVersion // Always use the new version
          };

          const requestPayload = {
            ClientConfiguration: JSON.stringify(mergedConfig),
            etag: existingConfig.etag
          };

          updateClientConfig(requestPayload)
            .unwrap()
            .catch((updateError: unknown) => {
              // 409 Conflict - concurrent update (expected race condition)
              if (
                updateError &&
                typeof updateError === "object" &&
                "status" in updateError &&
                (updateError as { status: number }).status === 409
              ) {
                return;
              }
            });
        }
      } catch (error) {
        const errorMessage = `Failed to parse client configuration. Data may be corrupted. (clientId: ${clientId})`;
        console.error("[useClientConfigSync]", errorMessage, {
          rawConfig: existingConfig.clientConfiguration,
          error
        });
        setParseError(errorMessage);
      }
    }
  }, [
    shouldQuery,
    clientId,
    createClientConfig,
    updateClientConfig,
    existingConfig,
    getError,
    isLoading,
    isUserLoggedIn,
    auth0Profile,
    authHeaderReady
  ]);

  return { parseError };
}
